import { Box, Button, List, ListItemButton, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { type CollectionBuilding } from '../../../../../models/peerToPeer/Collection'
import { type BuildingCompletion, completionText } from './buildingCompletion'
import { styles } from './BuildingsWithPane.styles'

interface Props {
  buildings: CollectionBuilding[]
  completion: Record<number, BuildingCompletion>
  selectedId: number
  onSelect: (organizationBuildingId: number) => void
  allComplete: boolean
  onBackToOverview: () => void
}

export const BuildingRail = ({ buildings, completion, selectedId, onSelect, allComplete, onBackToOverview }: Props): JSX.Element => (
  <Box sx={styles.rail}>
    <Box sx={styles.railHeader}>
      <Typography variant='h5' sx={styles.railHeaderText}>Buildings</Typography>
    </Box>
    <Box sx={styles.railHint}>
      <Typography variant='body2' sx={{ color: 'text.primary' }}>Select a building to fill out its information.</Typography>
    </Box>
    <List disablePadding sx={styles.railList}>
      {buildings.map(building => {
        const status = completion[building.organizationBuildingId]
        return (
          <ListItemButton
            key={building.organizationBuildingId}
            selected={building.organizationBuildingId === selectedId}
            onClick={() => { onSelect(building.organizationBuildingId) }}
            data-testid={`p2pPaneBuilding-${building.organizationBuildingId}`}
            sx={styles.railItem}
          >
            <Box sx={styles.railItemTick}>
              {(status?.isComplete ?? false) && <CheckCircleIcon fontSize='small' sx={{ color: 'var(--green-500)' }} />}
            </Box>
            <Box>
              <Typography variant='body1' sx={{ fontWeight: 600 }}>{building.name}</Typography>
              <Typography variant='caption' color='text.secondary'>{completionText(status)}</Typography>
            </Box>
          </ListItemButton>
        )
      })}
    </List>
    {allComplete &&
      <Box data-testid='p2pAllBuildingsComplete' sx={styles.completeCard}>
        <CheckCircleIcon sx={{ color: 'var(--green-500)', fontSize: '48px' }} />
        <Typography variant='subtitle1' sx={{ color: 'var(--white)', fontWeight: 700, mt: '8px' }}>All buildings complete!</Typography>
        <Typography variant='body2' sx={{ color: 'var(--white)', mt: '4px', mb: '16px' }}>Head back to the overview to review and submit your report.</Typography>
        <Button
          fullWidth variant='contained' startIcon={<ArrowBackIcon />} onClick={onBackToOverview}
          data-testid='p2pAllCompleteBackToOverview' sx={styles.completeButton}
        >
          Back to Overview
        </Button>
      </Box>}
  </Box>
)
